/**
 * Load and open Razorpay Checkout modal.
 *
 * Razorpay ships a global script (`https://checkout.razorpay.com/v1/checkout.js`)
 * that exposes `window.Razorpay`. We load it lazily on first use to keep the
 * main bundle small.
 */
declare global {
  interface Window {
    Razorpay?: any;
  }
}

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function loadScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Razorpay script failed to load")));
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Razorpay script failed to load"));
    document.head.appendChild(s);
  });
}

export interface RazorpaySuccess {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export async function openRazorpayCheckout(opts: {
  order_id: string;
  amount: number;         // in paise
  currency: "INR";
  key: string;            // Razorpay Key ID (public)
  name: string;
  description: string;
  prefill?: { email?: string; name?: string; contact?: string };
}): Promise<RazorpaySuccess | null> {
  await loadScript();
  return new Promise((resolve, reject) => {
    if (!window.Razorpay) {
      return reject(new Error("Razorpay SDK unavailable"));
    }
    const rzp = new window.Razorpay({
      key: opts.key,
      order_id: opts.order_id,
      amount: opts.amount,
      currency: opts.currency,
      name: opts.name,
      description: opts.description,
      prefill: opts.prefill || {},
      theme: { color: "#F97316" },
      handler: (resp: RazorpaySuccess) => resolve(resp),
      modal: {
        ondismiss: () => resolve(null),
      },
    });
    rzp.on("payment.failed", () => resolve(null));
    rzp.open();
  });
}
